/* Resumen corto de una aprobacion (headline + detail).
 *
 * Extraido de ApprovalList para poder reusarlo tambien en las notificaciones
 * del navegador (useApprovalsInbox). Va en su propio modulo — no en el .tsx del
 * componente — porque la regla eslint `react-refresh/only-export-components`
 * prohibe exportar funciones no-componente desde ficheros de componentes.
 */
import type { ApprovalItem } from "@/lib/api/approvalsApi";
import { formatMoney, normalizeCurrency } from "@/lib/approvals/money";

/**
 * El "titular" del item: la entidad principal (proveedor/cliente), que va en su
 * propia linea porque suele ser largo. Generico: el primero que el agente traiga.
 */
const SUMMARY_HEADLINE_KEYS = ["proveedor", "cliente"];
/**
 * Identificadores cortos del item para la 2ª linea de datos (nº · fecha · total).
 * Cortos: caben varios en una linea. Generico: se usan los que el agente traiga.
 */
const SUMMARY_DETAIL_KEYS = ["numero", "fecha", "total", "base"];
/** Campos cuyo valor se formatea como importe (coma decimal + divisa de los args). */
const SUMMARY_MONEY_KEYS = new Set(["total", "base", "iva", "importe"]);

function cleanMarkdownLine(line: string): string {
  return line
    .replace(/^[#>*\-\s]+/, "")                    // marcadores de inicio (#, >, *, -)
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")        // links [texto](url) -> texto
    .replace(/(\*\*|__|\*|_)(.*?)\1/g, "$2")         // enfasis inline
    .trim();
}

const isScalar = (v: unknown) => v !== null && v !== undefined && v !== "" && typeof v !== "object";

/** Formatea el valor de un campo para el resumen (importes con coma + divisa).
 * Sin divisa en los args (items antiguos), el importe va sin sufijo — no se asume €. */
function fmtSummaryValue(key: string, value: unknown, currency: string): string {
  if (SUMMARY_MONEY_KEYS.has(key) && typeof value === "number" && Number.isFinite(value)) {
    return formatMoney(value, currency);
  }
  return String(value);
}

/**
 * Resumen del item para la lista, en dos partes para no aplastar el titular:
 * - headline: la entidad principal (proveedor/cliente), en su propia linea.
 * - detail:   identificadores cortos (nº · fecha · total) en una segunda linea.
 * Prioriza DATOS de los args para identificar la factura de un vistazo; si no hay
 * escalares utiles, cae a la 1ª linea con texto de la descripcion markdown.
 * Generico: sirve para cualquier agente (usa los campos que traiga).
 */
export function shortSummary(item: ApprovalItem): { headline: string; detail: string } {
  const args = (item.payload.action_request?.args ?? {}) as Record<string, unknown>;

  // Divisa de la factura (código ISO): en resueltas manda la POSTEADA
  // (result.moneda) sobre la de los args (el revisor pudo editarla al aprobar).
  const currency =
    normalizeCurrency(item.result?.moneda) || normalizeCurrency(args.moneda);
  const headlineKey = SUMMARY_HEADLINE_KEYS.find((k) => isScalar(args[k]));
  const detail = SUMMARY_DETAIL_KEYS
    .filter((k) => isScalar(args[k]))
    .map((k) => fmtSummaryValue(k, args[k], currency))
    .join(" · ");

  if (headlineKey || detail) {
    let headline = headlineKey ? String(args[headlineKey]) : detail;
    // Si no hubo headline propio, el detail ya es el titular: no duplicar.
    const detailLine = headlineKey ? detail : "";
    if (headline.length > 80) headline = `${headline.slice(0, 77)}…`;
    return { headline, detail: detailLine };
  }

  // Fallback generico: primera linea con texto de la descripcion (sin encabezados).
  const desc = item.payload.description;
  if (desc) {
    const firstUseful = desc.split("\n").map(cleanMarkdownLine).find((l) => l.length > 0);
    if (firstUseful) {
      const h = firstUseful.length > 80 ? `${firstUseful.slice(0, 77)}…` : firstUseful;
      return { headline: h, detail: "" };
    }
  }
  return { headline: "", detail: "" };
}
